import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import incomeAPI from '@/services/api/income';
import expenseAPI from '@/services/api/expense';

import { useDashboardStore } from '@/store/useDashboardStore';

import DeleteMonthModal, { MonthToDelete } from './DeleteMonthModal';

/* Owns the whole month-deletion interface: confirmation modal, API call and
   store refresh — a page only says which resource it lists and when to open */
export const useDeleteMonth = (resource: 'income' | 'expenses') => {
  const { t } = useTranslation();

  const financialYear = useDashboardStore((state) => state.financialYear);
  const setIncomes = useDashboardStore((state) => state.setIncomes);
  const setExpenses = useDashboardStore((state) => state.setExpenses);

  const [monthToDelete, setMonthToDelete] = useState<MonthToDelete | null>(null);

  const deleteMonth = async () => {
    if (!monthToDelete) return;

    const params = { year: financialYear, month: monthToDelete.monthIndex + 1 };

    if (resource === 'income') {
      await incomeAPI.deleteMonth(params);
      setIncomes((await incomeAPI.index({ year: financialYear })).incomes);
    } else {
      await expenseAPI.deleteMonth(params);
      setExpenses((await expenseAPI.index({ year: financialYear })).expenses);
    }

    setMonthToDelete(null);
  };

  const confirmText = resource === 'income'
    ? t('income.confirmDeleteMonth', { month: monthToDelete?.name })
    : t('expenses.confirmDeleteMonth', { month: monthToDelete?.name });

  const warningText = resource === 'income'
    ? t('income.deleteMonthWarning')
    : t('expenses.deleteMonthWarning');

  const modal = (
    <DeleteMonthModal
      month={monthToDelete}
      confirmText={confirmText}
      warningText={warningText}
      onConfirm={deleteMonth}
      onClose={() => setMonthToDelete(null)}
    />
  );

  return { modal, requestMonthDelete: setMonthToDelete };
};

export default useDeleteMonth;
